import React from 'react';
import { IGlobalStorefrontSettings, IStorefrontLink, IStorefrontSettings, IStorefrontTheme } from '../../../models/Storefront';
import './StorefrontEditor.css';
import StorefrontHeaderEditor from './Header/StorefrontHeaderEditor';
import StorefrontFooterEditor from './Footer/StorefrontFooterEditor';
import { CommandBar, DefaultButton, ICommandBarItemProps, Icon, Panel, PanelType, Stack, Toggle } from '@fluentui/react';
import {
    EditPencil,
    ElementPropertyRows,
    elementStyleFrom,
    OPTION_ITEM_STYLES,
    renderOptionWithDescription,
    StyleOverridesGroup,
    StylePartAccordions,
} from './styleControls';
import { stylePartForConfigKey, styleTargetForConfigKey } from './styleBulkApply';
import { STYLE_PART_ICONS } from './stylesConfig';
import { resolvedStyleValue } from './styleConfig';
import { lintStyleSections } from './headerFooterConfig';
import RawJsonEditor from './RawJsonEditor';
import { ICartPageConfig, CART_PAGE_SECTIONS, parseCartPageConfig, serializeCartPageConfig } from './cartPageConfig';
import { IStorefrontProductView, IStorefrontBundleView } from '../../../models/StorefrontProducts';
import PreviewCanvas from './PreviewCanvas';

// Canonical child-editor prop contract shared by every page editor (see StorefrontPagesEditorPage).
interface IStorefrontCartPageEditorProps {
    globalSettings: IGlobalStorefrontSettings;
    globalTheme?: IStorefrontTheme;
    storefrontSettings: IStorefrontSettings;
    storefrontTheme?: IStorefrontTheme;
    editMode?: boolean;
    onStorefrontSettingsChange: (newSettings: IStorefrontSettings) => void;
    storefrontHeaderLinks: IStorefrontLink[];
    globalHeaderLinks: IStorefrontLink[];
    onHeaderLinksChange: (newLinks: IStorefrontLink[]) => void;
    storefrontFooterLinks: IStorefrontLink[];
    globalFooterLinks: IStorefrontLink[];
    onFooterLinksChange: (newLinks: IStorefrontLink[]) => void;
    sampleProducts?: IStorefrontProductView[];
    sampleBundles?: IStorefrontBundleView[];
}

// Only the style-section keys (not the openInPanel behavior flag) get style controls.
type SectionKey = typeof CART_PAGE_SECTIONS[number];

// The command bar's four topical panels (this replaced an 11-button toolbar laid out in four
// labeled columns). Each panel owns a group of the page's style sections as one-open-at-a-time
// accordions, and the one panel with a preview setting attached (Empty Cart) carries it above
// them. The page's single behavior flag styles nothing, so it lives in the far-side Options menu
// rather than pretending to be a style panel.
type PanelKey = 'page' | 'items' | 'summary' | 'empty';

const money = (n: number) => `$${n.toFixed(2)}`;

// Is the resolved page background dark? The shopper custom themes re-skin panel boxes
// (.cart-summary, .order-details-table-box, etc.) as translucent tints of the foreground
// — white-tint on dark, black-tint on light — so the theme text stays readable. The preview
// mirrors that so boxes don't render as stark solid-white blocks on a dark storefront.
function isColorDark(color?: string): boolean {
    if (!color) return false;
    let r = 0, g = 0, b = 0;
    const c = color.trim();
    if (c.startsWith('#')) {
        let hex = c.slice(1);
        if (hex.length === 3) hex = hex.split('').map((x) => x + x).join('');
        if (hex.length < 6) return false;
        r = parseInt(hex.slice(0, 2), 16); g = parseInt(hex.slice(2, 4), 16); b = parseInt(hex.slice(4, 6), 16);
    } else {
        const m = c.match(/rgba?\(([^)]+)\)/);
        if (!m) return false;
        const p = m[1].split(',').map((v) => parseFloat(v));
        r = p[0] || 0; g = p[1] || 0; b = p[2] || 0;
    }
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}

const StorefrontCartPageEditor: React.FC<IStorefrontCartPageEditorProps> = (props) => {
    const backgroundColor = resolvedStyleValue(props.storefrontSettings, 'backgroundColor') || props.storefrontTheme?.backgroundColor || props.globalSettings.backgroundColor || props.globalTheme?.backgroundColor || '#fff';
    const textColor = resolvedStyleValue(props.storefrontSettings, 'textColor') || props.storefrontTheme?.textColor || props.globalSettings.textColor || props.globalTheme?.textColor || '#242424';
    const defaultFont = resolvedStyleValue(props.storefrontSettings, 'defaultFont') || props.globalSettings.defaultFont || undefined;

    // Theme-aware panel tints (see isColorDark): translucent foreground on dark, subtle on light.
    const isDark = isColorDark(backgroundColor);
    const summaryBg = isDark ? 'rgba(255,255,255,0.2)' : 'white';
    const subtleBg = isDark ? 'rgba(255,255,255,0.1)' : '#fff';
    const subtleBorder = isDark ? 'rgba(255,255,255,0.2)' : '#d1d1d1';
    const thumbBg = isDark ? 'rgba(255,255,255,0.1)' : '#f3f2f1';
    // Line-item row cells carry a subtle tint on the real storefront (theme override of .checkout-*-row td).
    const rowBg = isDark ? 'rgba(255,255,255,0.06)' : '#fff';
    // Cart table text: the real storefront's dark theme forces `.checkout-table { color: #ddd }`
    // (a neutral near-white), so the cart is NOT tinted by the merchant's body textColor there.
    // Mirror that — neutral on dark, inherited body textColor on light (which has no theme override).
    const cartTextColor = isDark ? '#ddd' : textColor;

    const config = React.useMemo(() => parseCartPageConfig(props.storefrontSettings.cartPageConfig), [props.storefrontSettings.cartPageConfig]);

    // One panel open at a time; its command bar button is highlighted while it is. `part` names the
    // accordion to expand, so a click on a previewed element (or its pencil) lands on that
    // element's own settings rather than on the panel's first group.
    const [openPanel, setOpenPanel] = React.useState<{ key: PanelKey; part?: string } | null>(null);
    const closePanel = () => setOpenPanel(null);
    const openAt = (key: PanelKey, part?: string) => setOpenPanel({ key, part });
    // Editor-only preview switch: shows the empty-cart state in place of the line items so the
    // empty state can be styled. It is not persisted to the storefront.
    const [showEmpty, setShowEmpty] = React.useState(false);

    const updateConfig = (patch: Partial<ICartPageConfig>) =>
        props.onStorefrontSettingsChange({ ...props.storefrontSettings, cartPageConfig: serializeCartPageConfig({ ...config, ...patch }) });
    const updateSection = (key: SectionKey, patch: Partial<ICartPageConfig[SectionKey]>) =>
        updateConfig({ [key]: { ...(config[key] ?? {}), ...patch } });

    // Resolved inline styles for the preview elements.
    // The part passed here is the one implied by the config key (stylePartForKey), so an element
    // carrying a $styleRef resolves against the right part of the referenced Storefront Style.
    const s = (key: SectionKey) => elementStyleFrom(config[key], stylePartForConfigKey('cartPageConfig', key));

    // Click-to-edit wiring for a previewed element: the whole element opens its panel with its own
    // accordion expanded, and the hover pencil is the visible affordance for the same action.
    // EVERY editable element here sits inside the click-to-edit page container (and several sit
    // inside a second editable besides: the quantity stepper in its row, the buttons in the summary
    // box), so the click always has to stop before it reaches an ancestor that would open a
    // different panel. EditPencil stops propagation on its own.
    const editable = (panel: PanelKey, part: SectionKey, what: string): React.HTMLAttributes<HTMLElement> => props.editMode
        ? {
            className: 'sf-pencil-host sf-editable',
            title: `Edit the ${what}`,
            onClick: (e) => { e.stopPropagation(); openAt(panel, part); },
        }
        : { className: 'sf-pencil-host' };
    const pencil = (panel: PanelKey, part: SectionKey, what: string) =>
        props.editMode ? <EditPencil onClick={() => openAt(panel, part)} title={`Edit ${what}`} /> : null;

    // The two accordion bodies. A part whose config key maps to a named Storefront Style
    // (checkoutButton / secondaryButton / quantityInput, per styleBulkApply's allow-list) leads
    // with the picker and folds its raw rows under "Advanced overrides"; every other part is the
    // bare flat row surface.
    const rowsPart = (key: SectionKey) => (
        <ElementPropertyRows settingKeyPrefix={`storefront.cartPage.${key}`} value={config[key]} onChange={(patch) => updateSection(key, patch)} />
    );
    const styledPart = (key: SectionKey) => (
        <StyleOverridesGroup
            rowLayout
            target={styleTargetForConfigKey('cartPageConfig', key)}
            value={config[key]}
            onChange={(patch) => updateSection(key, patch)}
        >
            <ElementPropertyRows settingKeyPrefix={`storefront.cartPage.${key}`} value={config[key]} onChange={(patch) => updateSection(key, patch)} />
        </StyleOverridesGroup>
    );

    // Inline translations of the shopper index.css cart rules (.checkout-table / .checkout-*-row /
    // .cart-summary / .cart-checkout-btn / .cart-summary-btn), so the preview matches the real page.
    const cellBase: React.CSSProperties = { background: rowBg, borderTop: '3px solid transparent', borderBottom: '3px solid transparent', transition: 'border-color 0.2s', verticalAlign: 'middle' };
    const cellFirst: React.CSSProperties = { ...cellBase, borderLeft: '3px solid transparent', borderRadius: '12px 0 0 12px', padding: '8px 0 8px 8px', width: 100 };
    const cellMid: React.CSSProperties = { ...cellBase, padding: 8 };
    const cellLast: React.CSSProperties = { ...cellBase, borderRight: '3px solid transparent', borderRadius: '0 12px 12px 0', padding: '8px 8px 8px 0', textAlign: 'right', whiteSpace: 'nowrap' };
    const thumb: React.CSSProperties = { width: 100, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5em', background: 'transparent', borderRadius: 8, color: 'inherit' };
    const secondaryBtnBase: React.CSSProperties = { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', border: `1px solid ${subtleBorder}`, borderRadius: 4, background: subtleBg, color: 'inherit', cursor: 'pointer' };
    // The quantity stepper facsimile of the shopper NumberInput. Its config styling (quantityInput)
    // spreads onto the bordered inner span; the click-to-edit wiring and its pencil live on an outer
    // wrapper so the stepper's overflow:hidden (which clips the rounded corners) doesn't also clip
    // the pencil.
    const stepperInner = (n: number) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', border: `1px solid ${subtleBorder}`, borderRadius: 4, overflow: 'hidden', fontSize: 14, ...s('quantityInput') }}>
            <span style={{ padding: '4px 8px', background: thumbBg }}>&minus;</span>
            <span style={{ padding: '4px 12px', minWidth: 24, textAlign: 'center' }}>{n}</span>
            <span style={{ padding: '4px 8px', background: thumbBg }}>+</span>
        </span>
    );
    const qtyStepper = (n: number, editableStepper = false) => editableStepper ? (
        <span {...editable('items', 'quantityInput', 'quantity input')} style={{ position: 'relative', display: 'inline-flex' }}>
            {pencil('items', 'quantityInput', 'quantity input')}
            {stepperInner(n)}
        </span>
    ) : stepperInner(n);

    // Real product/bundle line items (falls back to the facsimile below when none are supplied).
    const prods = props.sampleProducts ?? [];
    const bundle = props.sampleBundles?.[0];
    const hasReal = prods.length > 0;
    const sampleQtys = [1, 2];
    const productRows = prods.slice(0, 2).map((p, i) => {
        const qty = sampleQtys[i] ?? 1;
        return { id: p.id, name: p.name, image: p.image, qty, lineTotal: (p.basePrice ?? 0) * qty };
    });
    const bundleQty = 1;
    const bundleLineTotal = bundle?.price ?? 0;
    const itemsCount = productRows.reduce((sum, r) => sum + r.qty, 0) + (bundle ? bundleQty : 0);
    const subtotal = productRows.reduce((sum, r) => sum + r.lineTotal, 0) + (bundle ? bundleLineTotal : 0);

    // Real image in the 100x100 thumb (contain-fit over the theme tint), else the emoji placeholder.
    const renderThumb = (image: string | undefined, emoji: string) =>
        image
            ? <div style={thumb}><img src={image} alt='' style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }} /></div>
            : <div style={thumb}>{emoji}</div>;

    // The Cart Page Style bar. One button, one panel; `checked` renders the pressed highlight while
    // that entry's panel is open (mirrors the Home, Search and Product page editors' bars). Every
    // entry carries a `title` tooltip saying what it opens: the bar's short labels name the topic,
    // but (unlike the labeled-columns toolbar this replaced) they don't describe the elements.
    const styleBarItems: ICommandBarItemProps[] = [
        { key: 'page', text: 'Page', title: 'The box around the whole cart page, and the "Cart" heading.', iconProps: { iconName: 'GridViewMedium' }, checked: openPanel?.key === 'page', onClick: () => openAt('page') },
        { key: 'items', text: 'Items', title: 'The line items: the box around them, the product and bundle rows, and the quantity stepper.', iconProps: { iconName: 'ProductList' }, checked: openPanel?.key === 'items', onClick: () => openAt('items') },
        { key: 'summary', text: 'Summary', title: 'The cart summary box on the right: its heading, the Checkout button and the secondary buttons.', iconProps: { iconName: 'Column' }, checked: openPanel?.key === 'summary', onClick: () => openAt('summary') },
        { key: 'empty', text: 'Empty Cart', title: 'What a shopper sees with nothing in the cart. Holds the preview switch that shows it here.', iconProps: { iconName: 'ShoppingCart' }, checked: openPanel?.key === 'empty', onClick: () => openAt('empty') },
    ];

    // Far-side menu: the page's one behavior switch, which styles nothing and so has no natural
    // home in a style panel (same placement rationale as the Search page's Options menu).
    const optionsBarItems: ICommandBarItemProps[] = [
        {
            key: 'options',
            text: 'Options',
            title: 'Page behavior settings: how the header cart icon opens the cart.',
            iconProps: { iconName: 'Settings' },
            subMenuProps: {
                items: [
                    {
                        key: 'openInPanel',
                        text: 'Open cart in slide-out panel',
                        canCheck: true,
                        checked: !!config.openInPanel,
                        onClick: () => updateConfig({ openInPanel: !config.openInPanel }),
                        onRenderContent: renderOptionWithDescription('When on, the header cart icon opens the cart in a right-side slide-out panel instead of navigating to the full cart page.'),
                        itemProps: { styles: OPTION_ITEM_STYLES },
                    },
                ],
            },
        },
    ];

    return (
        <div>
            {props.editMode && (
                <div style={{ marginBottom: '1em' }}>
                <RawJsonEditor
                    label='Cart Page'
                    liveObject={config}
                    onApply={(json) => props.onStorefrontSettingsChange({ ...props.storefrontSettings, cartPageConfig: json })}
                    // The shared section linter checks every CSS leaf with CSS.supports(); the one
                    // behavior boolean is named so it validates as itself rather than warning as an
                    // unknown key.
                    lint={(raw) => lintStyleSections(raw, CART_PAGE_SECTIONS, ['openInPanel'])}
                    keysHint={`Sections: ${CART_PAGE_SECTIONS.join(', ')}. Behavior keys: openInPanel (boolean). Any CSS property (camelCase, e.g. boxShadow) works. Numbers become px (use a string for other units, e.g. "600" for font-weight) and fontSize is a percent. url(), @import, javascript: and expression() are blocked. Changes apply while the JSON is valid.`}
                >
                    <CommandBar
                        items={styleBarItems}
                        farItems={optionsBarItems}
                        styles={{ root: { padding: 0, border: '1px solid #edebe9', borderRadius: 4, background: '#faf9f8' } }}
                    />
                </RawJsonEditor>
                {/* Per-topic style panels: right-side flyouts (like the home section editor).
                    Non-blocking so the preview stays live; the open panel's command bar button is
                    highlighted. Each is the same flat surface every other style panel uses: a
                    part accordion over flat property rows, one scroll, no tabs.
                    The React `key` re-seeds the open accordion when a SECOND element of the same
                    panel is clicked while that panel is already open: initialOpenKey is a seed, so
                    without the remount the accordion would stay on the first element. */}
                <Panel isOpen={openPanel?.key === 'page'} onDismiss={closePanel} headerText='Page' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={[
                        { key: 'container', label: 'Page Container', iconName: STYLE_PART_ICONS.band, tooltip: 'The box around the whole cart page: its background, border and inner padding.', content: rowsPart('container') },
                        { key: 'title', label: 'Title', iconName: STYLE_PART_ICONS.heading, tooltip: 'The "Cart" heading at the top of the page.', content: rowsPart('title') },
                    ]} />
                </Panel>
                {/* Items panel: the line-item table, from the box around it down to the stepper */}
                <Panel isOpen={openPanel?.key === 'items'} onDismiss={closePanel} headerText='Items' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={[
                        { key: 'rowsContainer', label: 'Rows Container', iconName: STYLE_PART_ICONS.band, tooltip: 'The box wrapping all the line-item rows, beside the summary box.', content: rowsPart('rowsContainer') },
                        { key: 'itemRow', label: 'Item Row', iconName: 'ProductList', tooltip: 'One product line in the cart.', content: rowsPart('itemRow') },
                        { key: 'bundleRow', label: 'Bundle Row', iconName: 'ProductList', tooltip: 'The bundle line, which lists its indented sub-items under the bundle name.', content: rowsPart('bundleRow') },
                        // Named-style target (a form input), so the picker leads and the raw rows
                        // fold away underneath it.
                        { key: 'quantityInput', label: 'Quantity Input', iconName: STYLE_PART_ICONS.input, tooltip: 'The per-line quantity stepper, shared by the desktop table and the mobile cards.', content: styledPart('quantityInput') },
                    ]} />
                </Panel>
                {/* Summary panel: the box on the right and everything inside it */}
                <Panel isOpen={openPanel?.key === 'summary'} onDismiss={closePanel} headerText='Summary' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <StylePartAccordions key={openPanel?.part} initialOpenKey={openPanel?.part} parts={[
                        { key: 'summaryBox', label: 'Summary Box', iconName: STYLE_PART_ICONS.band, tooltip: 'The cart summary panel on the right: its background, border and inner padding.', content: rowsPart('summaryBox') },
                        { key: 'summaryHeading', label: 'Heading', iconName: STYLE_PART_ICONS.heading, tooltip: 'The "Cart Summary" heading inside that box.', content: rowsPart('summaryHeading') },
                        // Both buttons are named-style targets, so each leads with the picker.
                        { key: 'checkoutButton', label: 'Checkout Button', iconName: STYLE_PART_ICONS.base, tooltip: 'The primary Checkout button in the summary box.', content: styledPart('checkoutButton') },
                        { key: 'secondaryButton', label: 'Secondary Buttons', iconName: STYLE_PART_ICONS.base, tooltip: 'The buttons under Checkout. Styles both Request a Quote and Share Cart.', content: styledPart('secondaryButton') },
                    ]} />
                </Panel>
                {/* Empty Cart panel: the preview switch is this panel's primary content (it is the
                    only way to see the empty state at all), so the style group below it starts
                    collapsed rather than burying it. */}
                <Panel isOpen={openPanel?.key === 'empty'} onDismiss={closePanel} headerText='Empty Cart' type={PanelType.smallFixedFar} isBlocking={false} closeButtonAriaLabel='Close'>
                    <Stack tokens={{ childrenGap: 8 }} style={{ marginTop: 12 }}>
                        {/* Editor-only: swaps the preview to the empty state. Nothing is saved. */}
                        <Toggle
                            label='Preview empty cart'
                            inlineLabel
                            checked={showEmpty}
                            onChange={(_, c) => setShowEmpty(!!c)}
                            styles={{ root: { marginBottom: 0 } }}
                        />
                    </Stack>
                    <StylePartAccordions startCollapsed key={openPanel?.part} initialOpenKey={openPanel?.part} parts={[
                        { key: 'emptyState', label: 'Empty Cart Style', iconName: 'ShoppingCart', tooltip: 'The block a shopper sees with nothing in the cart: the icon, the message and the Go Home button.', content: rowsPart('emptyState') },
                    ]} />
                </Panel>
                </div>
            )}

            <PreviewCanvas style={{ backgroundColor }}>
                <StorefrontHeaderEditor
                    storefrontSettings={props.storefrontSettings}
                    storefrontTheme={props.storefrontTheme}
                    globalSettings={props.globalSettings}
                    globalTheme={props.globalTheme}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                    editMode={false}
                    storefrontLinks={props.storefrontHeaderLinks}
                    globalLinks={props.globalHeaderLinks}
                    onLinksChange={props.onHeaderLinksChange}
                />

                <div style={{ color: cartTextColor, fontFamily: defaultFont, padding: '1.5em' }}>
                    {/* Page container — mirrors .mainContent (max-width 1200, centered) */}
                    <div {...editable('page', 'container', 'page container')} style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', ...s('container') }}>
                        {pencil('page', 'container', 'page container')}
                        <h1 {...editable('page', 'title', 'page title')} style={{ position: 'relative', ...s('title') }}>{pencil('page', 'title', 'title')}Cart</h1>

                        {showEmpty ? (
                            <div {...editable('empty', 'emptyState', 'empty cart block')} style={{ textAlign: 'center', padding: '3em 1em', position: 'relative', ...s('emptyState') }}>
                                {pencil('empty', 'emptyState', 'empty cart block')}
                                <div style={{ fontSize: '8em', color: '#ddd', lineHeight: 1 }}>🛒</div>
                                <h1 style={{ marginBottom: '0.5em' }}>Your cart is empty.</h1>
                                <DefaultButton primary text='Go Home' style={{ marginTop: '1em' }} />
                            </div>
                        ) : (
                            /* Real layout: line-items table (left) beside the .cart-summary box (right) */
                            <div style={{ display: 'flex', gap: '1em', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <div {...editable('items', 'rowsContainer', 'rows container')} style={{ flex: '1 1 340px', minWidth: 0, position: 'relative', ...s('rowsContainer') }}>
                                    {pencil('items', 'rowsContainer', 'rows container')}
                                    {/* .checkout-table: rows spaced via border-spacing, pill-rounded transparent-bordered cells */}
                                    <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
                                        <thead>
                                            <tr style={{ textAlign: 'left' }}>
                                                <th style={{ width: 100 }} />
                                                <th style={{ padding: 8 }}>Product</th>
                                                <th style={{ padding: 8, width: 150 }}>Quantity</th>
                                                <th style={{ padding: 8, width: 100, textAlign: 'center' }}>Price</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hasReal ? (
                                                <>
                                                    {/* .checkout-product-row — real storefront products.
                                                        Only the first row is click-to-edit: every row
                                                        shares the one itemRow config. */}
                                                    {productRows.map((r, i) => (
                                                        i === 0 ? (
                                                            <tr key={r.id} {...editable('items', 'itemRow', 'item row')} style={{ position: 'relative', ...s('itemRow') }}>
                                                                <td style={cellFirst}>{pencil('items', 'itemRow', 'item row')}{renderThumb(r.image, '📦')}</td>
                                                                <td style={cellMid}><h4 style={{ margin: 0 }}>{r.name}</h4></td>
                                                                <td style={cellMid}>{qtyStepper(r.qty, true)}</td>
                                                                <td style={cellLast}>{money(r.lineTotal)}</td>
                                                            </tr>
                                                        ) : (
                                                            <tr key={r.id} style={{ ...s('itemRow') }}>
                                                                <td style={cellFirst}>{renderThumb(r.image, '📦')}</td>
                                                                <td style={cellMid}><h4 style={{ margin: 0 }}>{r.name}</h4></td>
                                                                <td style={cellMid}>{qtyStepper(r.qty)}</td>
                                                                <td style={cellLast}>{money(r.lineTotal)}</td>
                                                            </tr>
                                                        )
                                                    ))}
                                                    {/* .checkout-bundle-row — bundle name + indented sub-items */}
                                                    {bundle && (
                                                        <tr {...editable('items', 'bundleRow', 'bundle row')} style={{ position: 'relative', ...s('bundleRow') }}>
                                                            <td style={cellFirst}>{pencil('items', 'bundleRow', 'bundle row')}{renderThumb(bundle.image, '📦')}</td>
                                                            <td style={cellMid}>
                                                                <h4 style={{ margin: 0 }}>{bundle.name}</h4>
                                                                {(bundle.products ?? []).map((cp) => (
                                                                    <p key={cp.id} style={{ margin: '2px 0', fontSize: '0.9em' }}>{cp.name} x {cp.quantity}</p>
                                                                ))}
                                                            </td>
                                                            <td style={cellMid}>{qtyStepper(bundleQty)}</td>
                                                            <td style={cellLast}>{money(bundleLineTotal)}</td>
                                                        </tr>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {/* .checkout-product-row */}
                                                    <tr {...editable('items', 'itemRow', 'item row')} style={{ position: 'relative', ...s('itemRow') }}>
                                                        <td style={cellFirst}>{pencil('items', 'itemRow', 'item row')}<div style={thumb}>📦</div></td>
                                                        <td style={cellMid}><h4 style={{ margin: 0 }}>24-Port Gigabit PoE+ Managed Switch</h4></td>
                                                        <td style={cellMid}>{qtyStepper(1, true)}</td>
                                                        <td style={cellLast}>{money(1650)}</td>
                                                    </tr>
                                                    <tr style={{ ...s('itemRow') }}>
                                                        <td style={cellFirst}><div style={thumb}>📦</div></td>
                                                        <td style={cellMid}><h4 style={{ margin: 0 }}>Wi-Fi 6 Access Point</h4></td>
                                                        <td style={cellMid}>{qtyStepper(2)}</td>
                                                        <td style={cellLast}>{money(240)}</td>
                                                    </tr>
                                                    {/* .checkout-bundle-row — bundle name + indented sub-items */}
                                                    <tr {...editable('items', 'bundleRow', 'bundle row')} style={{ position: 'relative', ...s('bundleRow') }}>
                                                        <td style={cellFirst}>{pencil('items', 'bundleRow', 'bundle row')}<div style={thumb}>📦</div></td>
                                                        <td style={cellMid}>
                                                            <h4 style={{ margin: 0 }}>Office Network Starter Kit</h4>
                                                            <p style={{ margin: '2px 0', fontSize: '0.9em' }}>8-Port Gigabit Switch x 1</p>
                                                            <p style={{ margin: '2px 0', fontSize: '0.9em' }}>Wi-Fi 6 Access Point x 1</p>
                                                        </td>
                                                        <td style={cellMid}>{qtyStepper(1)}</td>
                                                        <td style={cellLast}>{money(499)}</td>
                                                    </tr>
                                                </>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* .cart-summary: padding 1em, border-radius 3px; translucent tint on dark themes */}
                                <div {...editable('summary', 'summaryBox', 'summary box')} style={{ flex: '0 0 260px', boxSizing: 'border-box', background: summaryBg, borderRadius: 3, padding: '1em', position: 'relative', ...s('summaryBox') }}>
                                    {pencil('summary', 'summaryBox', 'summary box')}
                                    <h2 {...editable('summary', 'summaryHeading', 'summary heading')} style={{ position: 'relative', marginTop: 0, ...s('summaryHeading') }}>{pencil('summary', 'summaryHeading', 'summary heading')}Cart Summary</h2>
                                    <p style={{ fontSize: '120%', margin: '0.5em 0' }}><strong>Items: </strong>{hasReal ? itemsCount : 4}</p>
                                    <p style={{ fontSize: '120%', margin: '0.5em 0' }}><strong>Subtotal: </strong>{money(hasReal ? subtotal : 2629)}</p>
                                    {/* .cart-checkout-btn — Fluent primary (brand #0f6cbd) */}
                                    <button {...editable('summary', 'checkoutButton', 'Checkout button')} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 12px', border: 'none', borderRadius: 4, background: '#0f6cbd', color: '#fff', fontWeight: 600, cursor: 'pointer', position: 'relative', ...s('checkoutButton') }}>{pencil('summary', 'checkoutButton', 'Checkout button')}<Icon iconName='PaymentCard' />Checkout</button>
                                    <div style={{ height: 24 }} />
                                    {/* .cart-summary-btn — Fluent secondary. Both buttons share the one
                                        secondaryButton config, so only the first carries the pencil. */}
                                    <button {...editable('summary', 'secondaryButton', 'secondary buttons')} style={{ ...secondaryBtnBase, position: 'relative', ...s('secondaryButton') }}>{pencil('summary', 'secondaryButton', 'secondary buttons')}<Icon iconName='TextDocument' />Request a Quote</button>
                                    <div style={{ height: 8 }} />
                                    <button style={{ ...secondaryBtnBase, ...s('secondaryButton') }}><Icon iconName='Link' />Share Cart</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <StorefrontFooterEditor
                    storefrontSettings={props.storefrontSettings}
                    storefrontTheme={props.storefrontTheme}
                    globalSettings={props.globalSettings}
                    globalTheme={props.globalTheme}
                    onStorefrontSettingsChange={props.onStorefrontSettingsChange}
                    editMode={false}
                    footerLinks={props.storefrontFooterLinks}
                    globalFooterLinks={props.globalFooterLinks}
                />
            </PreviewCanvas>
        </div>
    );
};

export default StorefrontCartPageEditor;
